import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Route, useLocation } from "react-router";
import { Toaster } from "sonner";
import AdminErrorMessage from "./components/shared/AdminErrorMessage";
import ClientErrorMessage from "./components/shared/ClientErrorMessage";
import Footer from "./components/shared/Footer";
import Navbar from "./components/shared/Navbar";
import cacheKeys from "./config/cache-keys";
import environment from "./config/environment";
import pages from "./config/pages";
import useLanguage from "./hooks/useLanguage";
import { SentryRootErrorBoundary, SentryRoutes } from "./lib/sentry";
import SingleAccountPage from "./pages/Admin/Account/SingleAccountPage";
import FormulaTest from "./pages/Admin/CoverageMatrix";
import DashboardPage from "./pages/Admin/Dashboard/DashboardPage";
import GValuePage from "./pages/Admin/Dashboard/GValuePage";
import CreateMainAccountPage from "./pages/Admin/MainAccount/CreateMainAccountPage";
import SingleMainAccountPage from "./pages/Admin/MainAccount/SingleMainAccountPage";
import { ProductDefinitionsRouteGate } from "./pages/Admin/ProductDefinitions/ProductDefinitionsRouteGate";
import CalculatorPage from "./pages/Home/ClientPortal/CalculatorPage";
import DocumentsPage from "./pages/Home/ClientPortal/DocumentsPage";
import AppService from "./services/AppService";

function App() {
	const { pathname } = useLocation();
	const service = new AppService();

	const { _hasHydrated, isLoading } = useLanguage();

	const isAdmin = pathname.includes("/admin");

	const auth = useQuery({
		queryKey: cacheKeys.AUTH,
		queryFn: async () => {
			try {
				return await service.getAuth();
			} catch (error) {
				if (error instanceof AxiosError) {
					console.error("AxiosError:", error);
					if (error.response?.status === 401) {
						window.location.href = new URL(
							"/v1/auth/login",
							environment.get("VITE_BASE_API_URL"),
						).toString();
					}
				}
				return Promise.reject(error);
			}
		},
		enabled: pathname.includes("/admin"),
		retry: false,
	});

	if (isAdmin && auth.isLoading) {
		return <div>Loading...</div>;
	}

	if (isAdmin && auth.isError) {
		return (
			<AdminErrorMessage
				message={
					<>
						Oisann, her skjedde det noe feil.
						<br />
						Feilmelding: {auth.error.message}
					</>
				}
			/>
		);
	}

	if (isAdmin && (!auth.data || !auth.data.Result)) {
		console.error("No auth data");
		return <p>Noe gikk galt. Ingen auth data.</p>;
	}

	if (!_hasHydrated || isLoading) {
		return <p>Loading language</p>;
	}

	return (
		<>
			{isAdmin && <Navbar />}
			<SentryRoutes>
				<Route
					path={pages.MissingGroup}
					element={
						<>
							<ClientErrorMessage message="Mangler gruppe" />
							<Footer />
						</>
					}
					errorElement={<SentryRootErrorBoundary />}
				/>
				<Route path={pages.Home()} element={<CalculatorPage />} />
				<Route
					path={pages.ClientPortalCalculator()}
					element={<CalculatorPage />}
				/>

				<Route
					path={pages.ClientPortalDocuments()}
					element={<DocumentsPage />}
				/>
				<Route
					path={pages.ClientPortalCalculator()}
					element={<CalculatorPage />}
				/>

				<Route path={pages.Admin} element={<DashboardPage />} />
				<Route path={pages.GValuePage} element={<GValuePage />} />
				<Route
					path={pages.AdminProductDefinitions}
					element={<ProductDefinitionsRouteGate />}
				/>
				<Route
					path={pages.AdminMainAccountCreate}
					element={<CreateMainAccountPage />}
				/>

				<Route
					path={pages.AdminMainAccountUpdate()}
					element={<CreateMainAccountPage />}
				/>

				<Route path={pages.AdminCoverageMatrix()} element={<FormulaTest />} />

				<Route
					path={pages.AdminMainAccountDetails()}
					element={<SingleMainAccountPage />}
				/>

				<Route path={pages.AdminAccount()} element={<SingleAccountPage />} />
			</SentryRoutes>
			<Toaster richColors position="top-center" />
		</>
	);
}

export default App;
